import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  EnvelopeIcon, 
  CalendarIcon,
  CameraIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { authService } from '../../services/auth';
import { getRoleDisplayName } from '../../utils/permissions';
import { formatDateToIST, formatDateTimeToIST } from '../../utils/dateUtils';
import { profilePictureAPI } from '../../services/api';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const currentUser = authService.getUser();
    setUser(currentUser);
    setLoading(false);
    
    // Load profile picture if user has one
    if (currentUser?.profile_picture_filename) {
      console.log('User has profile picture, loading...', currentUser.profile_picture_filename);
      loadProfilePicture();
    } else {
      console.log('No profile picture found for user');
    }
  }, []);

  const loadProfilePicture = async () => {
    try {
      setProfilePictureLoading(true);
      const response = await profilePictureAPI.get();
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setProfilePicture(url);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      // Don't show error toast for 404 (no profile picture)
      if (error.response?.status !== 404) {
        window.showToast('Failed to load profile picture', 'error');
      }
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        window.showToast('Please select an image file', 'error');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        window.showToast('File size must be less than 5MB', 'error');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;

    try {
      setProfilePictureLoading(true);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64String = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
          
          const uploadData = {
            profile_picture: base64String,
            profile_picture_filename: selectedFile.name,
            profile_picture_content_type: selectedFile.type
          };
          
          await profilePictureAPI.upload(uploadData);
          window.showToast('Profile picture uploaded successfully', 'success');
          
          // Update user data in localStorage
          const updatedUser = { ...user, profile_picture_filename: selectedFile.name, profile_picture_content_type: selectedFile.type };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          
          // Set the profile picture directly from the uploaded file
          const fileUrl = URL.createObjectURL(selectedFile);
          setProfilePicture(fileUrl);
          setSelectedFile(null);
        } catch (error) {
          console.error('Error uploading profile picture:', error);
          window.showToast('Failed to upload profile picture', 'error');
        } finally {
          setProfilePictureLoading(false);
        }
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Error processing file:', error);
      window.showToast('Failed to process file', 'error');
      setProfilePictureLoading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      setProfilePictureLoading(true);
      await profilePictureAPI.delete();
      window.showToast('Profile picture deleted successfully', 'success');
      
      // Update user data in localStorage
      const updatedUser = { ...user, profile_picture_filename: null, profile_picture_content_type: null };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      setProfilePicture(null);
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      window.showToast('Failed to delete profile picture', 'error');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">User not found</h2>
          <p className="text-gray-400">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const profileFields = [
    {
      label: 'Email',
      value: user.email || 'Not provided',
      icon: EnvelopeIcon,
      color: 'text-green-600'
    },
    {
      label: 'Account Created',
      value: formatDateToIST(user.created_at),
      icon: CalendarIcon,
      color: 'text-indigo-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Profile</h1>
          <p className="mt-2 text-gray-400">View your account information</p>
        </div>

        {/* Profile Card */}
        <div className="bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-700">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
            <div className="flex items-center">
              <div className="flex-shrink-0 relative">
                <div className="h-20 w-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center overflow-hidden">
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="h-full w-full object-cover rounded-full"
                    />
                  ) : (
                    <UserIcon className="h-10 w-10 text-white" />
                  )}
                </div>
                
                {/* Profile Picture Upload/Delete Controls */}
                <div className="absolute -bottom-1 -right-1 flex space-x-1">
                  <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1.5 shadow-lg transition-colors">
                    <CameraIcon className="h-3 w-3" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="profile-picture-upload"
                    />
                  </label>
                  
                  {profilePicture && (
                    <button
                      onClick={handleDeleteProfilePicture}
                      disabled={profilePictureLoading}
                      className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded-full p-1.5 shadow-lg transition-colors"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="ml-6 flex-1">
                <h2 className="text-2xl font-bold text-white">
                  {user.full_name || user.username}
                </h2>
                <p className="text-blue-100">
                  {getRoleDisplayName(user.role)}
                </p>
                
                {/* File Selection and Upload */}
                {selectedFile && (
                  <div className="mt-3 p-2 sm:p-3 bg-white bg-opacity-10 rounded-lg">
                    <p className="text-white text-xs sm:text-sm mb-2 truncate">
                      Selected: {selectedFile.name}
                    </p>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                      <button
                        onClick={handleUploadProfilePicture}
                        disabled={profilePictureLoading}
                        className="px-3 py-1.5 sm:py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white text-xs sm:text-sm rounded transition-colors"
                      >
                        {profilePictureLoading ? 'Uploading...' : 'Upload'}
                      </button>
                      <button
                        onClick={() => setSelectedFile(null)}
                        disabled={profilePictureLoading}
                        className="px-3 py-1.5 sm:py-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white text-xs sm:text-sm rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profileFields.map((field, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 ${field.color}`}>
                    <field.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-400">{field.label}</p>
                    {field.badge ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${field.badgeColor}`}>
                        {field.value}
                      </span>
                    ) : (
                      <p className="text-sm text-gray-200 mt-1">{field.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Info Card */}
        <div className="mt-6 bg-gray-800 shadow rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-medium text-white mb-4">Account Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-400">Last Login:</span>
              <span className="ml-2 text-gray-200">
                {formatDateTimeToIST(user.last_login)}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Account Status:</span>
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 